import React, { useEffect, useState } from 'react';
import { updatePaymentLinkRecipient } from './paymentLinksStore';

const EMPTY = {
    recipient_name: '',
    recipient_phone: '',
    recipient_email: '',
    note: '',
};

export default function ModalEditPaymentLinkRecipient({ isOpen, onClose, link, onSuccess }) {
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState('');

    useEffect(() => {
        if (!isOpen || !link) return;
        setForm({
            recipient_name: link.recipient_name || '',
            recipient_phone: link.recipient_phone || '',
            recipient_email: link.recipient_email || '',
            note: link.note || '',
        });
        setErrors({});
        setFormError('');
    }, [isOpen, link]);

    if (!isOpen) return null;

    const setField = (name, value) => {
        setForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: undefined }));
        setFormError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (loading) return;

        const email = form.recipient_email.trim();
        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setErrors({ recipient_email: 'Adresse e-mail invalide.' });
            return;
        }

        setLoading(true);
        setFormError('');
        try {
            const updated = await updatePaymentLinkRecipient(link.id, {
                recipient_name: form.recipient_name.trim(),
                recipient_phone: form.recipient_phone.trim(),
                recipient_email: email,
                note: form.note.trim(),
            });
            onSuccess?.(updated, 'Destinataire mis à jour.');
            onClose();
        } catch (err) {
            const apiErrors = err?.response?.data?.errors;
            if (apiErrors && typeof apiErrors === 'object') {
                const next = {};
                Object.entries(apiErrors).forEach(([key, messages]) => {
                    next[key] = Array.isArray(messages) ? messages[0] : String(messages);
                });
                setErrors(next);
            } else {
                setFormError(
                    err?.response?.data?.message ||
                        'Impossible de mettre à jour le destinataire.',
                );
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-sm px-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-recipient-title"
            onClick={(e) => {
                if (e.target === e.currentTarget && !loading) onClose();
            }}
        >
            <div className="w-full max-w-md rounded-xl bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700 p-5 sm:p-6">
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                        <h2
                            id="edit-recipient-title"
                            className="text-lg font-bold text-gray-800 dark:text-gray-100"
                        >
                            Destinataire
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                            Ces infos sont optionnelles — utiles pour le suivi.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={loading}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 disabled:opacity-50"
                        aria-label="Fermer"
                    >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-3.5">
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1.5">
                            Nom
                        </label>
                        <input
                            type="text"
                            value={form.recipient_name}
                            onChange={(e) => setField('recipient_name', e.target.value)}
                            placeholder="Ex. Aya Koné"
                            maxLength={120}
                            className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2.5 text-sm text-gray-800 dark:text-gray-100"
                        />
                        {errors.recipient_name && (
                            <p className="text-xs text-rose-600 mt-1">{errors.recipient_name}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1.5">
                            Téléphone
                        </label>
                        <input
                            type="tel"
                            value={form.recipient_phone}
                            onChange={(e) => setField('recipient_phone', e.target.value)}
                            placeholder="Ex. 07 00 00 00 00"
                            maxLength={40}
                            className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2.5 text-sm text-gray-800 dark:text-gray-100"
                        />
                        {errors.recipient_phone && (
                            <p className="text-xs text-rose-600 mt-1">{errors.recipient_phone}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1.5">
                            E-mail
                        </label>
                        <input
                            type="email"
                            value={form.recipient_email}
                            onChange={(e) => setField('recipient_email', e.target.value)}
                            placeholder="Ex. aya@email.com"
                            maxLength={180}
                            className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2.5 text-sm text-gray-800 dark:text-gray-100"
                        />
                        {errors.recipient_email && (
                            <p className="text-xs text-rose-600 mt-1">{errors.recipient_email}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1.5">
                            Note
                        </label>
                        <textarea
                            value={form.note}
                            onChange={(e) => setField('note', e.target.value)}
                            placeholder="Contexte interne (VIP, table, etc.)"
                            rows={3}
                            maxLength={1000}
                            className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2.5 text-sm text-gray-800 dark:text-gray-100 resize-none"
                        />
                        {errors.note && (
                            <p className="text-xs text-rose-600 mt-1">{errors.note}</p>
                        )}
                    </div>

                    {formError && (
                        <p className="text-sm text-rose-600 dark:text-rose-400">{formError}</p>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            className="px-3.5 py-2 rounded-lg text-sm font-semibold border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                        >
                            {loading ? 'Enregistrement…' : 'Enregistrer'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
